<template>
  <div class="season-rollover-page">
    <div class="page-header">
      <h2>Season Rollover</h2>
      <p class="lead">
        Plan the move of players between squads at the end of a season. The
        system proposes moves based on each player's date of birth and the
        squad's U-grade range — you review and apply.
      </p>
    </div>

    <!-- Not an admin → access denied -->
    <div v-if="!isAdmin" class="alert alert-warning">
      This page is only available to admins.
    </div>

    <template v-else>
      <!-- ============ TOP CONTROLS ============ -->
      <div class="rollover-controls">
        <div class="control-row">
          <label for="from-season">From season:</label>
          <b-form-select
            id="from-season"
            v-model="fromSeason"
            :options="fromSeasonOptions"
            class="season-select"
          />
        </div>
        <div class="control-row">
          <label for="to-season">To season:</label>
          <b-form-select
            id="to-season"
            v-model="toSeason"
            :options="seasonOptions"
            class="season-select"
          />
        </div>
        <b-button
          variant="primary"
          :disabled="loading"
          @click="loadPlan"
        >
          {{ loading ? "Loading…" : plan ? "Refresh plan" : "Plan rollover" }}
        </b-button>
      </div>

      <!-- ============ PLAN RESULTS ============ -->
      <div v-if="plan" class="rollover-plan">
        <!-- Summary banner -->
        <div class="plan-summary">
          <strong>{{ toSeason }} season</strong> — reference year
          {{ plan.referenceYear }}.
          <span class="badge badge-secondary">{{ plan.staysPut.count }} stay put</span>
          <span class="badge badge-warning">{{ plan.ageOut.length }} aging out</span>
          <span class="badge badge-info">{{ plan.ageIn.length }} intake gaps</span>
          <span v-if="plan.needsReview.length" class="badge badge-danger">
            {{ plan.needsReview.length }} need review
          </span>
        </div>

        <!-- ===== STAYS PUT ===== -->
        <section class="plan-section">
          <h3
            class="section-header"
            @click="toggleSection('staysPut')"
          >
            <span class="caret">{{ openSections.staysPut ? "▼" : "▶" }}</span>
            Stays put ({{ plan.staysPut.count }} players — no action needed)
          </h3>
          <div v-if="openSections.staysPut" class="section-body">
            <p v-if="plan.staysPut.count === 0" class="text-muted">
              No players stay put — every player either ages out or needs review.
            </p>
            <ul v-else class="stays-put-grid">
              <li
                v-for="(count, grade) in plan.staysPut.byGrade"
                :key="grade"
              >
                <strong>{{ grade }}</strong>: {{ count }} player{{ count === 1 ? "" : "s" }}
              </li>
            </ul>
          </div>
        </section>

        <!-- ===== AGING OUT ===== -->
        <section class="plan-section">
          <h3
            class="section-header"
            @click="toggleSection('ageOut')"
          >
            <span class="caret">{{ openSections.ageOut ? "▼" : "▶" }}</span>
            Aging out ({{ plan.ageOut.length }} player{{ plan.ageOut.length === 1 ? "" : "s" }})
          </h3>

          <div v-if="openSections.ageOut" class="section-body">
            <p v-if="plan.ageOut.length === 0" class="text-muted">
              No players are aging out this rollover.
            </p>

            <template v-else>
              <div class="bulk-controls">
                <b-form-checkbox
                  :model-value="allSelectableChecked"
                  @update:model-value="toggleSelectAll"
                >
                  Select all with auto-suggestion
                </b-form-checkbox>
                <span class="text-muted">
                  ({{ selectedMoveCount }} of {{ plan.ageOut.length }} selected)
                </span>
              </div>

              <table class="age-out-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Player</th>
                    <th>DOB</th>
                    <th>Current squad</th>
                    <th>New U-grade</th>
                    <th>Move to</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in visibleAgeOutRows"
                    :key="row.userId"
                    :class="{ 'needs-manual': row.needsManualChoice }"
                  >
                    <td>
                      <b-form-checkbox
                        v-model="row._checked"
                        :disabled="row._destination === 'skip'"
                      />
                    </td>
                    <td>{{ row.name }}</td>
                    <td class="dob-cell">{{ formatYearOnly(row.dob) }}</td>
                    <td>{{ row.currentTeamName }}</td>
                    <td>
                      <strong>U{{ row.newUGrade }}</strong>
                    </td>
                    <td>
                      <b-form-select
                        v-model="row._destination"
                        :options="destinationOptions"
                        :class="{ 'manual-required': row.needsManualChoice && row._destination === 'skip' }"
                        @change="onDestinationChange(row)"
                      />
                    </td>
                    <td>
                      <button
                        class="dismiss-btn"
                        title="Hide from this plan"
                        @click="dismissRow(row)"
                      >✕</button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="apply-row">
                <b-button
                  variant="success"
                  :disabled="selectedMoveCount === 0"
                  @click="openApplyConfirm"
                >
                  Apply {{ selectedMoveCount }} selected move{{ selectedMoveCount === 1 ? "" : "s" }}
                </b-button>
              </div>
            </template>
          </div>
        </section>

        <!-- ===== INTAKE GAPS ===== -->
        <section class="plan-section">
          <h3
            class="section-header"
            @click="toggleSection('ageIn')"
          >
            <span class="caret">{{ openSections.ageIn ? "▼" : "▶" }}</span>
            Intake gaps ({{ plan.ageIn.length }} squad{{ plan.ageIn.length === 1 ? "" : "s" }} expect new birth year{{ plan.ageIn.length === 1 ? "" : "s" }})
          </h3>
          <div v-if="openSections.ageIn" class="section-body">
            <p v-if="plan.ageIn.length === 0" class="text-muted">
              Every squad has at least one player in each of its expected birth years.
            </p>
            <ul v-else class="intake-list">
              <li v-for="gap in plan.ageIn" :key="gap.teamId">
                <strong>{{ gap.teamName }}</strong>
                expects birth year{{ gap.expectedBirthYears.length === 1 ? "" : "s" }}
                {{ gap.expectedBirthYears.join(", ") }}.
                Missing:
                <span class="missing-years">{{ gap.missingBirthYears.join(", ") }}</span>
              </li>
            </ul>
            <p class="text-muted small">
              Add new players via the normal registration flow — this list just
              flags which birth years have no representation yet.
            </p>
          </div>
        </section>

        <!-- ===== NEEDS REVIEW ===== -->
        <section v-if="plan.needsReview.length" class="plan-section">
          <h3
            class="section-header"
            @click="toggleSection('review')"
          >
            <span class="caret">{{ openSections.review ? "▼" : "▶" }}</span>
            Needs review ({{ plan.needsReview.length }})
          </h3>
          <div v-if="openSections.review" class="section-body">
            <ul class="review-list">
              <li v-for="item in plan.needsReview" :key="item.userId">
                <strong>{{ item.name }}</strong> — {{ item.reason }}
              </li>
            </ul>
          </div>
        </section>
      </div>

      <!-- ============ RECENT BATCHES / ROLLBACK ============ -->
      <section class="plan-section recent-batches">
        <h3
          class="section-header"
          @click="toggleSection('batches')"
        >
          <span class="caret">{{ openSections.batches ? "▼" : "▶" }}</span>
          Recent rollover batches
        </h3>
        <div v-if="openSections.batches" class="section-body">
          <p v-if="recentBatches.length === 0" class="text-muted">
            No rollover batches have been applied yet.
          </p>
          <table v-else class="batches-table">
            <thead>
              <tr>
                <th>Applied</th>
                <th>From → To</th>
                <th>Moves</th>
                <th>Archived</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in recentBatches" :key="b.id">
                <td>{{ formatDateTime(b.applied_at) }}</td>
                <td>{{ b.from_season_year }} → {{ b.to_season_year }}</td>
                <td>{{ b.moves_count }}</td>
                <td>{{ b.archived_count }}</td>
                <td>
                  <span v-if="b.rolled_back_at" class="badge badge-secondary">
                    Rolled back {{ formatDateTime(b.rolled_back_at) }}
                  </span>
                  <span v-else-if="Number(b.eligible) === 1" class="badge badge-success">
                    Active (rollback eligible)
                  </span>
                  <span v-else class="badge badge-secondary">Sealed (>7 days)</span>
                </td>
                <td>
                  <b-button
                    v-if="!b.rolled_back_at && Number(b.eligible) === 1"
                    variant="outline-danger"
                    size="sm"
                    @click="openRollbackConfirm(b)"
                  >Roll back</b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- ============ APPLY CONFIRMATION MODAL ============ -->
    <b-modal
      v-if="showApplyModal"
      v-model="showApplyModal"
      size="lg"
      title="Confirm rollover"
      :hide-footer="true"
    >
      <p>
        You're about to apply <strong>{{ selectedMoveCount }} squad change{{ selectedMoveCount === 1 ? "" : "s" }}</strong>
        for the {{ toSeason }} season.
      </p>
      <ul class="confirm-list">
        <li v-for="m in confirmedMoves" :key="m.userId">
          <strong>{{ m.name }}</strong>:
          <span>{{ m.currentTeamName }}</span>
          →
          <span>{{ describeDestination(m) }}</span>
        </li>
      </ul>
      <p class="text-muted small">
        You can roll this batch back within 7 days using the "Recent rollover batches" panel.
      </p>
      <div class="modal-actions">
        <b-button variant="secondary" @click="showApplyModal = false">Cancel</b-button>
        <b-button variant="success" :disabled="applying" @click="confirmApply">
          {{ applying ? "Applying…" : "Apply now" }}
        </b-button>
      </div>
    </b-modal>

    <!-- ============ ROLLBACK CONFIRMATION MODAL ============ -->
    <b-modal
      v-if="showRollbackModal"
      v-model="showRollbackModal"
      size="md"
      title="Roll back batch?"
      :hide-footer="true"
    >
      <p v-if="rollbackTarget">
        Roll back the rollover applied on
        <strong>{{ formatDateTime(rollbackTarget.applied_at) }}</strong>
        ({{ rollbackTarget.moves_count }} move{{ rollbackTarget.moves_count === 1 ? "" : "s" }},
        {{ rollbackTarget.archived_count }} archived)?
      </p>
      <p class="text-muted small">
        This reopens the previous squad memberships, restores any archived
        players, and resets each player's primary team. Records created
        against the new squads since this batch was applied (training
        sessions, attendance, etc.) are <strong>not</strong> reverted.
      </p>
      <div class="modal-actions">
        <b-button variant="secondary" @click="showRollbackModal = false">Cancel</b-button>
        <b-button variant="danger" :disabled="rollingBack" @click="confirmRollback">
          {{ rollingBack ? "Rolling back…" : "Roll back" }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SeasonRollover } from "@/services/season-rollover-service.js";
import {
  uGradeForDob,
} from "@/utils/seasonGradeHelper.js";
import { getSeasonWindow } from "@/config/tenant";

// Season-string math that works for BOTH tenant season styles, detected
// from the string itself: cross-year "2025-26" (UK) and calendar "2026"
// (Australian grassroots). Mirrors SeasonPhases::next/previousSeasonYear.
function nextSeasonYear(season) {
  if (!season) return null;
  const cal = /^(\d{4})$/.exec(season);
  if (cal) return String(parseInt(cal[1], 10) + 1);
  const cross = /^(\d{4})-(\d{2})$/.exec(season);
  if (cross) {
    const start = parseInt(cross[1], 10);
    return `${start + 1}-${String((start + 2) % 100).padStart(2, "0")}`;
  }
  return null;
}

function previousSeasonYear(season) {
  if (!season) return null;
  const cal = /^(\d{4})$/.exec(season);
  if (cal) return String(parseInt(cal[1], 10) - 1);
  const cross = /^(\d{4})-(\d{2})$/.exec(season);
  if (cross) {
    const start = parseInt(cross[1], 10);
    return `${start - 1}-${String(start % 100).padStart(2, "0")}`;
  }
  return null;
}

// Current season label for THIS tenant (calendar or cross-year), from the
// tenant season config — replaces the old hardcoded cross-year assumption.
function currentSeasonYear() {
  return getSeasonWindow().label;
}

export default {
  name: "SeasonRollover",
  data() {
    return {
      userData: { userType: "", clubId: "" },
      fromSeason: currentSeasonYear(),
      toSeason: nextSeasonYear(currentSeasonYear()),
      plan: null,
      loading: false,
      applying: false,
      rollingBack: false,
      showApplyModal: false,
      showRollbackModal: false,
      rollbackTarget: null,
      recentBatches: [],
      openSections: {
        staysPut: false,
        ageOut: true,
        ageIn: true,
        review: true,
        batches: false,
      },
      dismissedRowIds: new Set(),
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isAdmin() {
      return this.userData.userType === "Admin";
    },
    fromSeasonOptions() {
      // Current season plus the previous two, so an admin who MISSED a
      // rollover can still select the season they need to catch up from.
      const current = currentSeasonYear();
      const prev1 = previousSeasonYear(current);
      const prev2 = previousSeasonYear(prev1);
      return [prev2, prev1, current]
        .filter(Boolean)
        .map((s) => ({ value: s, text: s }));
    },
    seasonOptions() {
      // "To" is derived from the selected "From": the next season and the
      // one after (allows planning ahead / a double catch-up).
      const next = nextSeasonYear(this.fromSeason);
      const after = nextSeasonYear(next);
      return [
        { value: next, text: next },
        { value: after, text: after },
      ];
    },
    /**
     * Dropdown options for each aging-out row:
     *   - "skip"     → don't move
     *   - "archive"  → set userType = Archived
     *   - numeric id → move to that squad
     */
    destinationOptions() {
      if (!this.plan) return [];
      const opts = [{ value: "skip", text: "— Don't move —" }];
      // List every squad in the club; the suggested one floats based on
      // each row's `_destination` value, set in onPlanLoaded.
      for (const s of this.plan.allSquads) {
        const range = s.u_grade_min !== null && s.u_grade_max !== null
          ? (s.u_grade_min === s.u_grade_max
              ? ` (U${s.u_grade_max})`
              : ` (U${s.u_grade_min}-U${s.u_grade_max})`)
          : "";
        opts.push({ value: Number(s.id), text: `${s.teamName}${range}` });
      }
      opts.push({ value: "archive", text: "Archive (set userType = Archived)" });
      return opts;
    },
    visibleAgeOutRows() {
      if (!this.plan) return [];
      return this.plan.ageOut.filter(r => !this.dismissedRowIds.has(r.userId));
    },
    selectedMoveCount() {
      return this.visibleAgeOutRows.filter(
        r => r._checked && r._destination !== "skip"
      ).length;
    },
    allSelectableChecked() {
      const selectable = this.visibleAgeOutRows.filter(r => r._destination !== "skip");
      if (selectable.length === 0) return false;
      return selectable.every(r => r._checked);
    },
    confirmedMoves() {
      return this.visibleAgeOutRows.filter(
        r => r._checked && r._destination !== "skip"
      );
    },
  },
  watch: {
    getProfile: {
      handler(profile) {
        if (profile) this.userData = { ...profile };
      },
      immediate: true,
    },
    // Keep "To" one season ahead of the selected "From", and clear any
    // stale plan so the admin re-plans for the new pairing.
    fromSeason(newFrom) {
      this.toSeason = nextSeasonYear(newFrom);
      this.plan = null;
    },
  },
  mounted() {
    if (this.isAdmin) this.loadRecentBatches();
  },
  methods: {
    toggleSection(key) {
      this.openSections = { ...this.openSections, [key]: !this.openSections[key] };
    },
    formatYearOnly(dob) {
      if (!dob) return "—";
      const m = /^(\d{4})/.exec(dob);
      return m ? m[1] : dob;
    },
    formatDateTime(dt) {
      if (!dt) return "";
      const d = new Date(dt.replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return dt;
      return d.toLocaleString();
    },
    async loadPlan() {
      if (!this.userData.clubId || !this.toSeason) return;
      this.loading = true;
      try {
        const resp = await SeasonRollover.find({
          action: "getRolloverPlan",
          clubId: this.userData.clubId,
          fromSeasonYear: this.fromSeason,
          toSeasonYear: this.toSeason,
        });
        if (resp?.status === 200 && resp.data && !resp.data.error) {
          this.onPlanLoaded(resp.data);
        } else {
          this.$toast.warning(
            resp?.data?.error || "Failed to load rollover plan.",
            { title: "Error", toaster: "b-toaster-bottom-right", solid: true, variant: "danger" }
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    onPlanLoaded(data) {
      // Decorate each aging-out row with UI-only fields _checked and
      // _destination so the table is fully self-contained. Rows with no
      // suggested squad (U19 graduates) start unchecked and with
      // destination = "skip" — admin must explicitly pick.
      const ageOut = (data.ageOut || []).map(r => ({
        ...r,
        _checked: !r.needsManualChoice,
        _destination: r.needsManualChoice ? "skip" : r.suggestedTeamId,
      }));
      this.plan = { ...data, ageOut };
      this.dismissedRowIds = new Set();
    },
    onDestinationChange(row) {
      // Switching to "skip" should auto-uncheck; picking any real destination
      // should auto-check (saves a click).
      if (row._destination === "skip") row._checked = false;
      else row._checked = true;
    },
    toggleSelectAll(value) {
      for (const r of this.visibleAgeOutRows) {
        if (r._destination !== "skip") r._checked = value;
      }
    },
    dismissRow(row) {
      this.dismissedRowIds = new Set([...this.dismissedRowIds, row.userId]);
    },
    openApplyConfirm() {
      this.showApplyModal = true;
    },
    describeDestination(row) {
      if (row._destination === "archive") return "Archive";
      const sq = this.plan.allSquads.find(s => Number(s.id) === Number(row._destination));
      return sq ? sq.teamName : "—";
    },
    async confirmApply() {
      this.applying = true;
      try {
        const moves = this.confirmedMoves.map(r => ({
          userId: r.userId,
          fromTeamId: r.currentTeamId,
          toTeamId: r._destination,
        }));
        const resp = await SeasonRollover.find({
          action: "applyRolloverMoves",
          clubId: this.userData.clubId,
          fromSeasonYear: this.fromSeason,
          toSeasonYear: this.toSeason,
          appliedByUserId: this.userData.id,
          moves,
        });
        if (resp?.status === 200 && resp.data && !resp.data.error) {
          this.$toast.info(
            `Applied ${resp.data.appliedCount} move(s), archived ${resp.data.archivedCount}.`,
            {
              title: "Rollover applied",
              toaster: "b-toaster-bottom-right",
              solid: true,
              variant: "success",
            }
          );
          this.showApplyModal = false;
          await this.loadRecentBatches();
          await this.loadPlan();
        } else {
          this.$toast.warning(
            resp?.data?.error || "Apply failed.",
            { title: "Error", toaster: "b-toaster-bottom-right", solid: true, variant: "danger" }
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.applying = false;
      }
    },
    async loadRecentBatches() {
      if (!this.userData.clubId) return;
      try {
        const resp = await SeasonRollover.find({
          action: "listRecentBatches",
          clubId: this.userData.clubId,
        });
        if (resp?.status === 200 && Array.isArray(resp.data)) {
          this.recentBatches = resp.data;
        }
      } catch (e) {
        console.error(e);
      }
    },
    openRollbackConfirm(batch) {
      this.rollbackTarget = batch;
      this.showRollbackModal = true;
    },
    async confirmRollback() {
      if (!this.rollbackTarget) return;
      this.rollingBack = true;
      try {
        const resp = await SeasonRollover.find({
          action: "rollbackLastRollover",
          batchId: this.rollbackTarget.id,
          rolledBackByUserId: this.userData.id,
        });
        if (resp?.status === 200 && resp.data && !resp.data.error) {
          this.$toast.info(
            `Rolled back ${resp.data.rolledBackCount} move(s), restored ${resp.data.restoredArchivedCount} archived player(s).`,
            {
              title: "Rollback complete",
              toaster: "b-toaster-bottom-right",
              solid: true,
              variant: "success",
            }
          );
          this.showRollbackModal = false;
          this.rollbackTarget = null;
          await this.loadRecentBatches();
          if (this.plan) await this.loadPlan();
        } else {
          this.$toast.warning(
            resp?.data?.error || "Rollback failed.",
            { title: "Error", toaster: "b-toaster-bottom-right", solid: true, variant: "danger" }
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.rollingBack = false;
      }
    },
    // Used in template; helps suppress lint warning about unused import.
    _uGradeForDob: uGradeForDob,
  },
};
</script>

<style scoped>
.season-rollover-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem;
}
.page-header h2 {
  margin-bottom: 0.25rem;
}
.lead {
  color: #6c757d;
  margin-bottom: 1.5rem;
}
.rollover-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}
.control-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.season-label {
  font-weight: bold;
}
.season-select {
  width: 8rem;
}
.plan-summary {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
}
.plan-summary .badge {
  margin-left: 0.5rem;
}
.plan-section {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 1rem;
}
.section-header {
  margin: 0;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 1rem;
  background: #f8f9fa;
  border-radius: 4px 4px 0 0;
  user-select: none;
}
.section-header:hover {
  background: #e9ecef;
}
.caret {
  display: inline-block;
  width: 1rem;
  color: #6c757d;
}
.section-body {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
}
.bulk-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}
.age-out-table {
  width: 100%;
  border-collapse: collapse;
}
.age-out-table th,
.age-out-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}
.age-out-table th {
  text-align: left;
  background: #f8f9fa;
  font-weight: 600;
}
.age-out-table tr.needs-manual {
  background: #fff8e1;
}
.dob-cell {
  font-variant-numeric: tabular-nums;
  color: #6c757d;
}
.manual-required {
  border-color: #ffc107;
  background: #fff8e1;
}
.dismiss-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}
.dismiss-btn:hover {
  color: #dc3545;
}
.apply-row {
  margin-top: 1rem;
  text-align: right;
}
.stays-put-grid {
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.5rem;
}
.intake-list,
.review-list {
  padding-left: 1.5rem;
}
.missing-years {
  color: #dc3545;
  font-weight: bold;
}
.batches-table {
  width: 100%;
  border-collapse: collapse;
}
.batches-table th,
.batches-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  text-align: left;
  vertical-align: middle;
}
.confirm-list {
  max-height: 320px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
}
.modal-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
